/**
* Abstract Web Service interface that serves as a basis for other
* Web Service interfaces and provides common functionality
*
* @class AbstractWS
*/

var AbstractViewModel = require("../viewmodels/AbstractViewModel")
var getQueryParam = require("../helpers/viewmodels").getQueryParam
var xmlToJson = require("./../helpers/xmlToJson")
var tracingAttachSpanToCurrentTransaction = require("../helpers/sentry").tracingAttachSpanToCurrentTransaction

var AbstractWS = function (parent) {
  var self = this;

  AbstractViewModel.apply(self, arguments);

  self.callback = null;
  self.data = null;
  self.dataType = 'xml';

  /** 
   * Handles failed requests (HTTP Status Error or Network)
   * @type {Function}
   */
  self.errorHandler = function () { };

  /**
   * Handles responses which contain Errors/Exceptions 
   * @type {Function} 
   **/
  self.processErrorHandler = function () {};

  /**
   * @type {Function}
   */
  self.alwaysHandler = null;

  self.err = null;
  self.errorMessage = ko.observable(null);
  self.name = 'AbstractWS';
  self.method = ko.observable('GET');
  self.params = {};
  self.parent = parent;
  self.retryCount = 0;
  self.serviceURL = ko.computed(function () {
    return '';
  });
  self.xmlToJson = xmlToJson
  self.getQueryParam = getQueryParam
  self._tracingSpan = null;
};

AbstractWS.prototype = Object.create(AbstractViewModel.prototype, { constructor: AbstractWS});

/**
 * Method to add parameters that will be passed to the api service call
 *
 * @method addParam
 * @param {String} sKey
 * @param {String} sName
 * @returns {AbstractWS}
 */
AbstractWS.prototype.addParam = function (sKey, sVal) {
  this.params[sKey] = sVal;

  return this;
};

/**
 * 
 * @param {Object} params a collection of parameters to be passed to the API
 */
AbstractWS.prototype.addParams = function(params) {
  this.params = Object.assign(this.params, params)
}


/**
 * Checks any POST request responses for errors, typically WSSIDException
 *
 * @method checkResponseForError
 * @param {Object} caller
 * @param {Object} callerArgs
 * @returns {Error|Null}
 */
AbstractWS.prototype.checkResponseForError = function (caller, callerArgs, data) {
  var cause = null;
  var err = null;
  var json = null;
  var self = this;

  if (typeof data === 'object') {
    json = self.xmlToJson.parse(data.response);
  }
  else {
    json = self.xmlToJson.parse(data);
  }

  self.errorMessage(null);

  // If an error was found, examine the type. If the type
  if (json && json.error) {
    err = json.error;

    cause = err.cause;

    // WSSIDException
    if (cause && cause.type && cause.type === 'WSSIDException') {
      self.retryCount++;

      if (self.retryCount < 5) {
        vm.wssidWS.doRequest();

        setTimeout(function() {
          caller.doRequest();
        },100);
      }
    }
    // Hum-dinger... could be anything. Just show the raw error msg
    else {

      // TODO: This is NOT a Hum-dinger. Linebuster will populate the XML response with the thrown Exception.
      // Here we find the **root** cause and use that as the Error but I'm not sure that should be expoosing that.
      // Ideally, the UI should use the nearest error?
      var errorText = err.text;
      if(cause) {
        errorText = cause.text;
        while(cause.cause && cause.cause !== cause) {
          cause = cause.cause;
          errorText = cause.text;
        }
      }
      self.errorMessage(errorText);
    }

  }
  else {
    // No error appears to have occured, change the retryCount back to 0
    if (caller) {
      caller.retryCount = 0;
    }
  }

  return err;
};



/**
 * Method to reset any params which might be passed to the service
 *
 * @method clearParams
 * @returns {AbstractWS}
 */
AbstractWS.prototype.clearParams = function () {
  this.params = {};
  return this;
};


/**
 * Method to remove a named parameter from the array of parameters
 *
 * @method deleteParam
 * @param {String} sKey
 * @returns {AbstractWS}
 */
AbstractWS.prototype.deleteParam = function (sKey) {
  delete this.params[sKey];
  return this;
};


/**
 * Default error handler.
 * 
 * rXHR, textStatus and errorThrown are remnants from when this application used jQuery's AJAX object
 * instead of reqwest.
 * 
 * jQuery's callback interface was slightly different but the argument names have been kept, for now,
 * as refactoring caution.
 * 
 * You can see how reqwest invokes callbacks:
 * {@link https://github.com/ded/reqwest/blob/22da21c5de609d5b75ee21eb4484bce42c474973/src/reqwest.js#L330}
 * {@link https://github.com/ded/reqwest/blob/22da21c5de609d5b75ee21eb4484bce42c474973/tests/tests.js#L362}
 *
 * @method _errorHandler
 * @param {object} rXHR contains the response, if the status code is an error. If the request failed, then it contains the request obj. reqwest uses `resp`
 * @param {string} textStatus an error message or status text from the response. reqwest uses `msg`
 * @param {string|object} errorThrown an exception object if the response was JSON and could not be parsed; could be anything otherwise, usually a string. reqwest uses `t`
 * @returns none
 */
AbstractWS.prototype._errorHandler = function (rXHR, textStatus, errorThrown) {
  var self = this;
  try {
    self.conlog(rXHR);
    if (errorThrown && errorThrown.toLowerCase() !== 'abort') {
      self.errorHandler(textStatus, errorThrown);
    }
    else {
      console.log(textStatus, errorThrown);
    }

    if (this._tracingSpan) {
      this._tracingSpan.setStatus("unknown_error")
      this._tracingSpan.finish()
    }
  }
  catch (e) {
    console.log("exception", e)
    window.Sentry && window.Sentry.captureException(e);
  }

  try {
    if (self.alwaysHandler) {
      self.alwaysHandler()
    }
  } catch (e) {
    window.Sentry && window.Sentry.captureException(e);
  }
};


/**
 * Requests data from the WS. Hands off processing to processData()
 *
 * @method doRequest
 * @param {requestSuccessHandler} successHandler a callback function invoked for successful requests.
 * @param {requestErrorHandler} processErrorHandler a callback function invoked if the request fails.
 * @param {Function} alwaysHandler function that is always invoked, if the request succeeds or fails.
 * @returns {AbstractWS}
 */
AbstractWS.prototype.doRequest = function (successHandler, processErrorHandler, alwaysHandler) {
  var self = this;
  var headers = null;

  // Ensure that this method is only called by a WS object derived from
  // this prototype. If not, exit gracefully.
  if (!self.name || self.name === 'AbstractWS' || !self.serviceURL()) {
    // Return self (AbstractWS) to be chainable, for best practice
    return self;
  }

  if (alwaysHandler && (typeof alwaysHandler == "function")) {
    self.alwaysHandler = alwaysHandler;
  }

  if (successHandler && (typeof successHandler == "function")) {
    self.callback = successHandler;
  }

  if (processErrorHandler && (typeof processErrorHandler == "function")) {
    self.processErrorHandler = processErrorHandler;
  }

  // Clear out any previous errors
  self.err = null;
  self.errorMessage(null);

  var timestamp = new Date().getTime();

  // Request or send data to the server (serviceURL). Once the data is
  // returned, run the data through the processData() method. Almost every
  // instance of this object will define it's own processData() method.
  // In case an error occured, run the errorHandler() method.
  headers = self.headers(timestamp);

  // Perform the call to the service
  // At the time of this authoring, data is being returned from the
  // service in XML format. It is hoped that data will be available
  // as JSON at a later date, at which time this prototype will require
  // updating.

  this._tracingSpan = tracingAttachSpanToCurrentTransaction({ op: "qless.cec.ws", description: this.name })

  $.ajax({
    url: this.timestampedURL(timestamp),
    headers: headers,
    method: self.method(),
    dataType: self.dataType,
    data: self.params,
    cache: false,
    success: function (data) {
      var processDataSpan = tracingAttachSpanToCurrentTransaction({ op: "qless.cec.ws.processdata", description: self.name })
      self.processData(data);

      if (processDataSpan) {
        processDataSpan.setStatus("ok");
        processDataSpan.finish();
      }

      if (self._tracingSpan) {
        self._tracingSpan.setStatus("ok");
        self._tracingSpan.finish();
      }
    },
    error: self._errorHandler.bind(self)
  });

  return self;
};

/**
 * We attach a timestamp to the end of each request to ensure that IE 11
 * will not cache each request.
 */
AbstractWS.prototype.timestampedURL = function(timestamp) {
  var hasQueryParams = ((this.serviceURL().indexOf('?') > 0) ? true : false)

  if (timestamp == undefined) {
    timestamp = new Date().getTime();
  }

  return (hasQueryParams) 
    ? this.serviceURL() + '&_=' + timestamp
    : this.serviceURL() + '?_=' + timestamp
}

/**
 * Invoked by a successful AJAX request
 * @callback requestSuccessHandler
 * @param {Object} data the data from the Response
 */

/**
 * Handles a failed AJAX request after AbstractWS's general error handling.
 * @callback requestErrorHandler
 * @param {string} textStatus the data from the Response
 * @param {Object} errorThrown the data from the Response
 */

/**
 * Calculate the headers to send. Would be nice to make this observable, but
 * as it has a dependency on the wssid(), it's fine as a function.
 *
 * @method headers
 * @returns {Object}
 */
AbstractWS.prototype.headers = function (timestamp) {
  var hdr = {};

  if (window.vm && window.vm.authInfoWS) {
    hdr.LOGGED_IN_EMPLOYEE = window.vm.authInfoWS.userId();
  }

  if (timestamp !== undefined) {
    hdr.REQUEST_TIMESTAMP = timestamp
  }

  if (this.method().toLowerCase() !== 'get') {
    hdr['X-WSSID-Authorization'] = vm.wssid();

    switch (this.dataType) {
      case 'json':
        hdr['Content-Type'] = 'application/json; charset=UTF-8';
        break;

      default:
        hdr['Content-Type'] = 'application/x-www-form-urlencoded; charset=UTF-8';
        break;
    }
  }

  return hdr;
};


/**
 * Processes the data returned from an API service call.
 * This method is regularly overridden by derived WS objects to
 * turn the resulting data into usable objects it represents.
 *
 * @method processData
 * @param {XMLString} data
 * @param {Boolean} bAvoidCallback
 * @returns {AbstractWS}
 */
AbstractWS.prototype.processData = function (data, bAvoidCallback) {
  var self = this;
  var err = null;

  // Store the data (XML) for console inspection
  if (self.getQueryParam('debug') === 'true') {
    self.data = data;
  }

  // If the request was a POST, make sure we didn't run into a WSSIDEXCEPTION
  if (self.shouldCheckForErrorResponse()) {
    err = self.checkResponseForError(self, null, data);
    self.err = err;
  }

  // If we know about a callback, run it and then clear the callback so it does
  // not get run again, unintented.
  if (!err) {
    setTimeout(function () {
      if (self.callback && bAvoidCallback !== true) {
        self.callback();
        self.callback = null;
      }
    }, 250);
  }

  if (err) {
    setTimeout(function () {
      self.processErrorHandler(self.err);
    })
  }

    if (self.alwaysHandler && (typeof self.alwaysHandler == "function")) {
      setTimeout(function () {
        self.alwaysHandler();
      }, 250);
  }

  // Return self (AbstractWS) to be chainable, for best practice
  return self;
};


/**
 * Method to be overridden by subclassed objects. Allows to custom
 * properties to be reset
 *
 * @method reset
 * @returns {AbstractWS}
 */
AbstractWS.prototype.reset = function () {
  var self = this;

  try {
    self.data = null;
    self.errorMessage(null);
    self.clearParams();
    self.retryCount = 0;
  }
  catch (e) {
    window.Sentry && window.Sentry.captureException(e);
  }

  return this;
};

AbstractWS.prototype.isLangUS = function () {
  return (navigator && navigator.languages && navigator.languages[0] != 'en-US');
}

/***
 * @typedef ErrorNode
 * @property {string} type
 * @property {string} text
 * @property {object} cause
 * @property {string} fieldName
 */

/**
 * Flattens the Error's nested Causes into a list where the first Cause
 * is first in the array and the last/deepest cause is last (right most) in the array.
 * @param {ErrorNode} error
 * @returns {Array<ErrorNode>}
 */
AbstractWS.prototype.errorCauses = function (error) {
  var causes = [];
  var cause = error.cause;

  if (cause && cause !== error) {
    causes.push(cause);
    return causes.concat(this.errorCauses(cause))
  }

  return causes;
}

AbstractWS.prototype.shouldCheckForErrorResponse = function () {
  return  this.method().toLowerCase() === "post"
}

module.exports = AbstractWS
